export default class Label {
    constructor(box21_api) {
        this.api = box21_api;
    }

    /**
     * Typeahead search for labels (uses POST /labels with limit).
     * @param {string} query substring
     * @param {{ type?: string|number|null, limit?: number }} opts type: 1 / 2 / 'box' / 'point' or omit for any
     */
    async labelSearch(query, opts = {}) {
        const limit = opts.limit != null ? opts.limit : 50;
        let filterType = opts.type;
        if (filterType === 'box') {
            filterType = 1;
        }
        if (filterType === 'point') {
            filterType = 2;
        }
        const formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('filter_name', query == null ? '' : String(query));
        if (filterType !== null && filterType !== undefined && filterType !== '') {
            formData.append('filter_type', String(filterType));
        }
        formData.append('limit', String(limit));
        formData.append('offset', '0');
        return this.api.post_with_token('/labels', formData);
    }

    async fetchByIds(labelIds) {
        if (!labelIds || labelIds.length === 0) {
            return [];
        }
        const formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('label_ids', JSON.stringify(labelIds));
        return this.api.post_with_token('/labels', formData);
    }

    async fetchByNames(names) {
        if (!names || names.length === 0) {
            return [];
        }
        const formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('label_names', JSON.stringify(names));
        return this.api.post_with_token('/labels', formData);
    }

    async fetchV2Page({
        page = 1,
        itemsPerPage = 1000,
        filter = '',
        parentFilter = '',
        ancestorId = null,
        directChildrenOnly = false,
        includeAncestor = false,
        sortBy = '',
        sortDesc = false,
    } = {}) {
        const formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('page', String(page));
        formData.append('itemsPerPage', String(itemsPerPage));
        if (filter) {
            formData.append('filter', filter);
        }
        if (parentFilter) {
            formData.append('parent_filter', parentFilter);
        }
        if (ancestorId != null && ancestorId !== '' && Number(ancestorId) > 0) {
            formData.append('ancestor_id', String(ancestorId));
            formData.append('direct_children_only', directChildrenOnly ? 'true' : 'false');
            formData.append('include_ancestor', includeAncestor ? 'true' : 'false');
        }
        if (sortBy) {
            formData.append('sortBy', String(sortBy));
            formData.append('sortDesc', sortDesc ? 'true' : 'false');
        }
        return this.api.post_with_token('/v2/labels', formData);
    }

    async fetchAllProjectLabelsV2() {
        const pageSize = 1000;
        let page = 1;
        const all = [];
        let total = Infinity;
        while (all.length < total) {
            const res = await this.fetchV2Page({ page, itemsPerPage: pageSize });
            if (!res) {
                break;
            }
            const items = res.items || [];
            if (typeof res.total === 'number') {
                total = res.total;
            } else if (!items.length) {
                break;
            }
            all.push(...items);
            if (items.length < pageSize) {
                break;
            }
            page += 1;
        }
        return all;
    }

    /**
     * Count labels in the project (cheap: one page of size 1).
     */
    async countProjectLabels() {
        const res = await this.fetchV2Page({ page: 1, itemsPerPage: 1 });
        if (res && typeof res.total === 'number') {
            return res.total;
        }
        return 0;
    }

    /**
     * Bulk-create labels (POST /labels/bulk). rows: {name, color?, type?, parent?}[]
     */
    async bulkAdd(rows) {
        if (!rows || rows.length === 0) {
            return { created: [], created_count: 0, skipped_count: 0 };
        }
        const formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('labels', JSON.stringify(rows));
        return this.api.post_with_token('/labels/bulk', formData);
    }

    async all(filter) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'))
        if(typeof filter != 'undefined') {
            if(filter.filter_name) {
                formData.append('filter_name', filter.filter_name);
            }
            if(filter.filter_type) {
                formData.append('filter_type', filter.filter_type);
            }
        }
        return this.api.post_with_token('/labels', formData);
    }

    async all_in_batches(filter) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'))
        if(typeof filter != 'undefined') {
            if(filter.filter_name) {
                formData.append('filter_name', filter.filter_name);
            }
            if(filter.filter_type) {
                formData.append('filter_type', filter.filter_type);
            }
        }

        let batch_size = 2000;
        let hasMore = true;
        let i = 0;
        let allLabels = [];
        while(hasMore) {
            formData.delete('offset');
            formData.delete('limit');
            formData.append('offset', batch_size * i);
            formData.append('limit', batch_size);

            let response = await this.api.post_with_token('/labels', formData);
            if(response.length == 0) {
                hasMore = false;
            }
            allLabels = allLabels.concat(response);
            i++;
        }
        return allLabels
    }

    async patch(label) {
        let formData = new FormData();
        formData.append('id', label.id)
        formData.append('name', label.name)
        formData.append('color', label.color == null ? '' : String(label.color))
        if (label.type != null && label.type !== '') {
            formData.append('type', String(label.type))
        }
        // Always send parent_id so clearing the parent persists (empty ⇒ null on API).
        if (label.parent_id != null && label.parent_id !== '' && label.parent_id !== false) {
            formData.append('parent_id', String(label.parent_id))
        } else {
            formData.append('parent_id', '')
        }
        const projectId = sessionStorage.getItem('project_id');
        if (projectId) {
            formData.append('project_id', projectId);
        }
        return this.api.post_with_token('/update-label', formData);
    }

    async merge(label_id, merge_label_id) {
        let formData = new FormData();
        formData.append('label_id', label_id)
        formData.append('merge_label_id', merge_label_id)
        const projectId = sessionStorage.getItem('project_id');
        if (projectId) {
            formData.append('project_id', projectId);
        }
        return this.api.post_with_token('/merge-label', formData);
    }

    async patch_labels(selected_label_ids, parent_id, color, type) {
        let formData = new FormData();
        formData.append('label_ids', JSON.stringify(selected_label_ids));
        if (parent_id !== undefined) {
            formData.append('parent_id', parent_id);
        }
        if (color !== undefined) {
            formData.append('color', color);
        }
        if (type !== undefined) {
            formData.append('type', type);
        }
        formData.append('project_id', sessionStorage.getItem('project_id'))
        return this.api.post_with_token('/labels/update', formData);
    }

    async delete(label) {
        let formData = new FormData();
        formData.append('label_id', label.id);
        formData.append('project_id', sessionStorage.getItem('project_id'))
        return this.api.post_with_token('/label/delete', formData)
    }

    async get_crop(label_name, project_id) {
        let formData = new FormData();
        formData.append('label_name', label_name);
        formData.append('project_id', project_id);
        return this.api.post_with_token('/get_crop_for_label', formData)
    }

    async get_random_crops(label_name, project_id, count = 4, exclude_bbox_ids = []) {
        let formData = new FormData();
        formData.append('label_name', label_name);
        formData.append('project_id', project_id);
        formData.append('count', String(count));
        if (exclude_bbox_ids && exclude_bbox_ids.length > 0) {
            formData.append('exclude_bbox_ids', JSON.stringify(exclude_bbox_ids));
        }
        return this.api.post_with_token('/get_random_crops_for_label', formData)
    }

    async add(label) {
        let formData = new FormData();
        formData.append('name', label.name);
        formData.append('type', label.type);
        formData.append('color', label.color);
        formData.append('project_id', sessionStorage.getItem('project_id'))
        if (label.parent_id != null && label.parent_id !== '' && label.parent_id !== false) {
            formData.append('parent_id', label.parent_id);
        }
        return this.api.post_with_token('/label/add', formData)
    }
}